/**
 * StockStatusBadge
 *
 * The single rendering of `Product.stockStatus`, which the API computes once
 * (InventoryController::stockStatusFor). Before this, the stock list, the
 * dashboard and the reports each re-derived status from raw quantities with
 * three different comparisons and disagreed with each other.
 *
 * Renders nothing for `ok` — a healthy row needs no chrome.
 */
import type { StockStatus } from '@/services/inventory.service';

const STATUS_LABELS: Record<StockStatus, string> = {
  out: 'Out of Stock',
  low: 'Low Stock',
  ok: 'In Stock',
};

const STATUS_CLASSES: Record<StockStatus, string> = {
  out: 'bg-red-100 text-red-800',
  low: 'bg-amber-100 text-amber-800',
  ok: 'bg-green-100 text-green-800',
};

export function stockStatusLabel(status: StockStatus): string {
  return STATUS_LABELS[status] ?? STATUS_LABELS.ok;
}

export default function StockStatusBadge({
  status,
  showOk = false,
  className = '',
}: {
  status: StockStatus;
  /** Render a badge for healthy stock too. Off by default. */
  showOk?: boolean;
  className?: string;
}) {
  if (status === 'ok' && !showOk) return null;

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status] ?? STATUS_CLASSES.ok} ${className}`}
    >
      {stockStatusLabel(status)}
    </span>
  );
}
